import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { COMPANY } from '../data/content';

// Navigation with subpages = actual sections on each main page
// Each subpage uses hash anchor so clicking scrolls to that section
const NAV_TREE = [
  {
    label: 'About Us',
    path: '/about',
    subpages: [
      { label: 'Who We Are', path: '/about#who-we-are' },
      { label: 'Areas of Innovation', path: '/about#innovation' },
      { label: 'Vision, Mission & Standards', path: '/about#principles' },
      { label: 'Our Legacy', path: '/about#legacy' },
      { label: 'Leadership', path: '/about#leadership' },
      { label: 'Guiding Principle', path: '/about#motto' },
    ],
  },
  {
    label: 'What We Do',
    path: '/services',
    subpages: [
      { label: 'Our Solutions', path: '/services#solutions' },
      { label: 'Our Products', path: '/services#products' },
      { label: 'How We Work', path: '/services#process' },
      { label: 'Tricolite EPC Advantage', path: '/services#epc-advantage' },
    ],
  },
  {
    label: 'Our Work',
    path: '/our-work',
    subpages: [
      { label: 'IT Data Centres', path: '/our-work#it' },
      { label: 'Automobile Industry', path: '/our-work#automobile' },
      { label: 'Food & Beverages', path: '/our-work#food' },
      { label: 'Hospitality Sector', path: '/our-work#hospitality' },
      { label: 'Infra Projects', path: '/our-work#infra' },
      { label: 'Other Factories & Buildings', path: '/our-work#other' },
    ],
  },
  {
    label: 'CSR',
    path: '/csr',
    subpages: [
      { label: 'Sadgati Foundation', path: '/csr#sadgati' },
      { label: 'Tree Plantation', path: '/csr#trees' },
      { label: 'Yoga Day', path: '/csr#yoga' },
    ],
  },
  { label: 'Careers', path: '/careers' },
  {
    label: 'Become a Subcontractor',
    path: '/subcontractors',
    subpages: [
      { label: 'Our Approach', path: '/subcontractors#approach' },
      { label: 'How to Register', path: '/subcontractors#register' },
    ],
  },
  { label: 'Contact', path: '/contact' },
];

// Page paths that have a full-viewport hero (so no spacer is needed under the fixed header).
// We also use this list to seed the navbar's "dark" state on initial render — the
// IntersectionObserver below then takes over for live section-aware updates.
const darkHeroPages = ['/', '/about', '/services', '/our-work', '/csr'];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeKey, setActiveKey] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  // `onDark` is true whenever the strip of viewport directly behind the fixed navbar
  // is occupied by a dark surface (hero with dark overlay, bg-[var(--ink)] section,
  // footer, etc.). Controlled by an IntersectionObserver below.
  const [onDark, setOnDark] = useState(() => darkHeroPages.includes(typeof window !== 'undefined' ? window.location.pathname : '/'));
  const closeTimer = useRef(null);
  const loc = useLocation();

  // Scroll progress bar
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      setProgress(Math.min(1, Math.max(0, window.scrollY / max)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Section-aware theme detection.
  // Every "dark surface" on the page is opted-in by adding the `data-nav-theme="dark"`
  // attribute (heros with dark overlays, bg-[var(--ink)] sections, the footer, etc.).
  // We watch them with a single IntersectionObserver whose root is shrunk to the top
  // 84px of the viewport — the navbar is "on dark" while at least one tagged surface
  // intersects that strip.
  useEffect(() => {
    const HEADER_H = 84;
    let darkCount = 0;
    const states = new WeakMap();

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const wasIn = states.get(e.target) === true;
        if (e.isIntersecting && !wasIn) { darkCount += 1; states.set(e.target, true); }
        else if (!e.isIntersecting && wasIn) { darkCount = Math.max(0, darkCount - 1); states.set(e.target, false); }
      });
      setOnDark(darkCount > 0);
    }, {
      // Shrink the viewport's bottom edge upward so only the top `HEADER_H` strip
      // counts as "intersecting" — i.e. we only care what's behind the navbar.
      rootMargin: `0px 0px -${Math.max(0, window.innerHeight - HEADER_H)}px 0px`,
      threshold: 0,
    });

    const collectAndObserve = () => {
      // Footer is always dark
      document.querySelectorAll('footer').forEach((el) => {
        if (!states.has(el)) { states.set(el, false); obs.observe(el); }
      });
      // Every element explicitly tagged as a dark surface
      document.querySelectorAll('[data-nav-theme="dark"]').forEach((el) => {
        if (!states.has(el)) { states.set(el, false); obs.observe(el); }
      });
    };

    // Re-collect a few times to catch sections that mount after route change.
    const timers = [0, 80, 300, 800].map((ms) => setTimeout(collectAndObserve, ms));

    return () => {
      timers.forEach(clearTimeout);
      obs.disconnect();
      // Reset to a sensible default on unmount/path-change so the next page paints
      // its initial theme correctly while the new observer is still warming up.
      darkCount = 0;
    };
  }, [loc.pathname]);

  useEffect(() => { setOpen(false); setActiveKey(null); }, [loc.pathname]);

  // Smooth scroll to section when hash exists
  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace('#', '');
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [loc.pathname, loc.hash]);

  if (loc.pathname.startsWith('/admin')) return null;

  const openMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setActiveKey(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveKey(null), 220);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 backdrop-blur-xl ${onDark ? 'border-b border-white/10 shadow-none' : 'border-b border-[var(--line)] shadow-[0_1px_0_rgba(14,22,32,0.04)]'}`}
        style={{ backgroundColor: onDark ? 'rgba(14, 22, 32, 0.32)' : 'rgba(246, 242, 235, 0.62)' }}
      >
        <div className="flex items-center justify-between px-6 md:px-10 lg:px-14 h-[84px]">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/logos/full.png" alt="Tricolite Energy Solutions" className="h-12 md:h-14 w-auto select-none"/>
          </Link>

          <nav className="hidden lg:flex items-center gap-0" data-testid="primary-nav">
            {NAV_TREE.map((n) => {
              const hasDropdown = !!n.subpages?.length;
              const isOpen = activeKey === n.path;
              return (
                <div
                  key={n.path}
                  className="relative"
                  onMouseEnter={() => hasDropdown && openMenu(n.path)}
                  onMouseLeave={() => hasDropdown && scheduleClose()}
                  data-testid={`nav-item-${n.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                >
                  <NavLink
                    to={n.path}
                    end={n.path === '/'}
                    className={({ isActive }) => `relative px-3.5 py-3 text-[13px] font-medium tracking-wide transition-colors flex items-center gap-1 ${isActive ? 'text-[var(--blue)]' : (onDark ? 'text-white hover:text-[var(--orange)]' : 'text-[var(--ink)] hover:text-[var(--blue)]')}`}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative">
                          {n.label}
                          {isActive && <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[var(--orange)]" />}
                        </span>
                        {hasDropdown && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                      </>
                    )}
                  </NavLink>

                  {hasDropdown && isOpen && (
                    <div
                      className="absolute top-full left-0 pt-2 z-50"
                      onMouseEnter={() => openMenu(n.path)}
                      onMouseLeave={scheduleClose}
                      data-testid={`dropdown-${n.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                    >
                      <div className="bg-white border border-[var(--line)] rounded-xl shadow-[0_10px_40px_-8px_rgba(14,22,32,0.18)] py-2 min-w-[260px] overflow-hidden">
                        <div className="px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[var(--text-light)] border-b border-[var(--line)] mb-1">
                          On this page
                        </div>
                        {n.subpages.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className="block px-5 py-2.5 text-[13.5px] text-[var(--ink)] hover:bg-[var(--cream)] hover:text-[var(--blue)] transition-colors"
                            data-testid={`dropdown-link-${sub.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden md:inline-flex items-center gap-2 bg-[var(--blue)] text-white pl-5 pr-6 py-3 rounded-full text-[13px] font-medium tracking-wide hover:bg-[var(--orange)] transition-colors group btn-shine" data-testid="cta-get-in-touch">
              <span className="btn-dot text-white"/>
              <span className="btn-label">Get In Touch</span>
              <ArrowUpRight className="w-4 h-4 btn-arrow" />
            </Link>
            <button className="lg:hidden w-11 h-11 rounded-full bg-[var(--blue)] text-white flex items-center justify-center" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--line)] bg-[var(--cream)] px-6 py-6" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV_TREE.map((n) => {
                const hasSubs = !!n.subpages?.length;
                const expanded = mobileExpanded === n.path;
                return (
                  <div key={n.path} className="border-b border-[var(--line)]">
                    <div className="flex items-center justify-between py-3">
                      <NavLink
                        to={n.path}
                        end={n.path === '/'}
                        className={({ isActive }) => `text-[15px] ${isActive ? 'text-[var(--blue)] font-semibold' : 'text-[var(--ink)]'}`}
                      >
                        {n.label}
                      </NavLink>
                      {hasSubs && (
                        <button
                          onClick={() => setMobileExpanded(expanded ? null : n.path)}
                          aria-label="Expand"
                          className="p-2 text-[var(--text-mid)]"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}/>
                        </button>
                      )}
                    </div>
                    {hasSubs && expanded && (
                      <div className="pb-3 pl-3 flex flex-col gap-2">
                        {n.subpages.map((sub) => (
                          <Link key={sub.path} to={sub.path} className="text-[13.5px] text-[var(--text-mid)] hover:text-[var(--blue)] py-1.5">
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link to="/contact" className="mt-5 inline-flex items-center justify-center gap-2 bg-[var(--blue)] text-white px-5 py-4 rounded-full text-[14px] font-medium">
                Get In Touch <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </header>
      {/* Scroll progress bar — sits at the very bottom edge of the fixed navbar */}
      <div aria-hidden="true" className="fixed left-0 right-0 z-[51] h-[2px] bg-transparent pointer-events-none" style={{ top: '84px' }}>
        <div className="h-full bg-[var(--orange)] origin-left transition-[width] duration-150 ease-out" style={{ width: `${progress * 100}%` }} />
      </div>
      {/* Spacer for pages without a full-viewport hero, so content isn't covered by the fixed header */}
      {!darkHeroPages.includes(loc.pathname) && <div aria-hidden="true" className="h-[84px]" />}
    </>
  );
}
